import { motion } from 'framer-motion'
import { Link } from 'react-router-dom'
import { 
  Crop, 
  Upload, 
  Download, 
  Smartphone, 
  Monitor, 
  Gamepad2, 
  Users, 
  Star,
  CheckCircle,
  ArrowRight,
  Image as ImageIcon,
  Scissors,
  Palette
} from 'lucide-react'
import { useIntl } from 'react-intl'
import { useLanguage } from '../contexts/LanguageContext'
import SEO from '../components/SEO'
import Hero from '../components/Hero'
import { messages } from '../i18n'

const Home = () => {
  const intl = useIntl()
  const { locale, getLocalizedPath } = useLanguage()

  // 获取数组类型的翻译
  const getArrayTranslation = (id: string): any[] => {
    const flat = messages[locale]
    const value = flat?.[id]
    return Array.isArray(value) ? value : []
  }

  const features = [
    {
      icon: <Upload className="h-8 w-8" />,
      title: intl.formatMessage({ id: 'home.features.upload.title' }),
      description: intl.formatMessage({ id: 'home.features.upload.description' })
    },
    {
      icon: <Scissors className="h-8 w-8" />,
      title: intl.formatMessage({ id: 'home.features.crop.title' }),
      description: intl.formatMessage({ id: 'home.features.crop.description' })
    },
    {
      icon: <Palette className="h-8 w-8" />,
      title: intl.formatMessage({ id: 'home.features.edit.title' }),
      description: intl.formatMessage({ id: 'home.features.edit.description' })
    },
    {
      icon: <Download className="h-8 w-8" />,
      title: intl.formatMessage({ id: 'home.features.download.title' }),
      description: intl.formatMessage({ id: 'home.features.download.description' })
    }
  ]

  const platforms = [
    { name: "Discord", size: "128x128", icon: <Users className="h-6 w-6" /> },
    { name: "Twitter", size: "400x400", icon: <Monitor className="h-6 w-6" /> },
    { name: "LinkedIn", size: "300x300", icon: <Monitor className="h-6 w-6" /> },
    { name: "Steam", size: "184x184", icon: <Gamepad2 className="h-6 w-6" /> },
    { name: "Instagram", size: "320x320", icon: <Smartphone className="h-6 w-6" /> },
    { name: "Facebook", size: "180x180", icon: <Monitor className="h-6 w-6" /> }
  ]

  // Get benefits array from messages using array translation function
  const benefits = getArrayTranslation('home.benefits.list')

  // Get testimonials array from messages using array translation function
  const testimonials = getArrayTranslation('home.testimonials.items').map((item: any) => ({
    ...item,
    rating: 5
  }))

  return (
    <>
      <SEO />
      
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.5 }}
      >
        <Hero />

        {/* Introduction Section with Image */}
        <section className="py-12 sm:py-16 lg:py-20 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 sm:gap-12 items-center">
              <motion.div
                initial={{ opacity: 0, x: -50 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8 }}
                viewport={{ once: true }}
                className="order-2 lg:order-1"
              >
                <h2 className="text-2xl sm:text-3xl md:text-4xl font-display font-bold text-gray-900 mb-4 sm:mb-6 leading-tight">
                  {intl.formatMessage({ id: 'home.introduction.title' })}
                </h2>
                <p className="text-base sm:text-lg text-gray-600 mb-4 sm:mb-6 leading-relaxed">
                  {intl.formatMessage({ id: 'home.introduction.description1' })}
                </p>
                <p className="text-base sm:text-lg text-gray-600 mb-6 sm:mb-8 leading-relaxed">
                  {intl.formatMessage({ id: 'home.introduction.description2' })}
                </p>
                <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
                  <Link to={getLocalizedPath('/cropper')} className="bg-primary-600 hover:bg-primary-700 active:bg-primary-800 text-white font-semibold py-3 px-6 rounded-lg transition-all duration-200 flex items-center justify-center space-x-2 touch-manipulation">
                    <Upload className="h-4 w-4 sm:h-5 sm:w-5" />
                    <span className="text-sm sm:text-base">{intl.formatMessage({ id: 'home.introduction.startCreating' })}</span>
                  </Link>
                  <Link to={getLocalizedPath('/faq')} className="border-2 border-primary-600 text-primary-600 hover:bg-primary-50 active:bg-primary-100 font-semibold py-3 px-6 rounded-lg transition-all duration-200 touch-manipulation text-sm sm:text-base">
                    {intl.formatMessage({ id: 'home.introduction.learnMore' })}
                  </Link>
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, x: 50 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8 }}
                viewport={{ once: true }}
                className="order-1 lg:order-2"
              >
                <div className="relative">
                  <img 
                    src="/avatar-cropper.avif" 
                    alt="Avatar Cropper Tool Interface - Professional photo editing made simple"
                    className="w-full h-auto rounded-2xl shadow-2xl"
                    loading="lazy"
                  />
                  <div className="absolute inset-0 bg-gradient-to-tr from-primary-600/20 to-transparent rounded-2xl"></div>
                </div>
              </motion.div>
            </div>
          </div>
        </section>

        {/* How It Works Section */}
        <section className="py-12 sm:py-16 lg:py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className="text-center mb-12 sm:mb-16"
            >
              <h2 className="text-2xl sm:text-3xl md:text-4xl font-display font-bold text-gray-900 mb-3 sm:mb-4 leading-tight">
                {intl.formatMessage({ id: 'home.howItWorks.title' })}
              </h2>
              <p className="text-base sm:text-lg lg:text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
                {intl.formatMessage({ id: 'home.howItWorks.description' })}
              </p>
            </motion.div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 sm:gap-8">
              {features.map((feature, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  className="card text-center hover:shadow-xl transition-all duration-300 touch-manipulation"
                >
                  <div className="inline-flex items-center justify-center w-12 h-12 sm:w-16 sm:h-16 bg-primary-100 text-primary-600 rounded-full mb-4 sm:mb-6">
                    {feature.icon}
                  </div>
                  <h3 className="text-lg sm:text-xl font-semibold text-gray-900 mb-3 sm:mb-4 leading-tight">{feature.title}</h3>
                  <p className="text-sm sm:text-base text-gray-600 leading-relaxed">{feature.description}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Platform Support Section */}
        <section className="py-12 sm:py-16 lg:py-20 gradient-bg">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className="text-center mb-12 sm:mb-16"
            >
              <h2 className="text-2xl sm:text-3xl md:text-4xl font-display font-bold text-gray-900 mb-3 sm:mb-4 leading-tight">
                {intl.formatMessage({ id: 'home.platforms.title' })}
              </h2>
              <p className="text-base sm:text-lg lg:text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
                {intl.formatMessage({ id: 'home.platforms.description' })}
              </p>
            </motion.div>

            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4 sm:gap-6">
              {platforms.map((platform, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, scale: 0.8 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  className="bg-white rounded-xl p-4 sm:p-6 text-center shadow-lg hover:shadow-xl transition-all duration-300 touch-manipulation"
                >
                  <div className="inline-flex items-center justify-center w-10 h-10 sm:w-12 sm:h-12 bg-secondary-100 text-secondary-600 rounded-full mb-3 sm:mb-4">
                    {platform.icon}
                  </div>
                  <h3 className="text-sm sm:text-base font-semibold text-gray-900 mb-1 sm:mb-2 leading-tight">{platform.name}</h3>
                  <p className="text-xs sm:text-sm text-gray-600">{platform.size}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Benefits Section */}
        <section className="py-12 sm:py-16 lg:py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 sm:gap-12 items-center">
              <motion.div
                initial={{ opacity: 0, x: -50 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8 }}
                viewport={{ once: true }}
              >
                <h2 className="text-2xl sm:text-3xl md:text-4xl font-display font-bold text-gray-900 mb-4 sm:mb-6 leading-tight">
                  {intl.formatMessage({ id: 'home.benefits.title' })}
                </h2>
                <p className="text-base sm:text-lg lg:text-xl text-gray-600 mb-6 sm:mb-8 leading-relaxed">
                  {intl.formatMessage({ id: 'home.benefits.description' })}
                </p>
                <ul className="space-y-3 sm:space-y-4">
                  {benefits.map((benefit, index) => (
                    <motion.li
                      key={index}
                      initial={{ opacity: 0, x: -20 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      transition={{ duration: 0.5, delay: index * 0.1 }}
                      viewport={{ once: true }}
                      className="flex items-start space-x-3"
                    >
                      <CheckCircle className="h-5 w-5 sm:h-6 sm:w-6 text-green-500 flex-shrink-0 mt-0.5" />
                      <span className="text-sm sm:text-base text-gray-700 leading-relaxed">{benefit}</span>
                    </motion.li>
                  ))}
                </ul>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, x: 50 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8 }}
                viewport={{ once: true }}
                className="relative mt-8 lg:mt-0"
              >
                <div className="bg-gradient-to-br from-primary-500 to-secondary-500 rounded-2xl p-6 sm:p-8 text-white">
                  <ImageIcon className="h-12 w-12 sm:h-16 sm:w-16 mb-4 sm:mb-6" />
                  <h3 className="text-xl sm:text-2xl font-bold mb-3 sm:mb-4 leading-tight">{intl.formatMessage({ id: 'home.benefits.card.title' })}</h3>
                  <p className="text-sm sm:text-base text-primary-100 mb-4 sm:mb-6 leading-relaxed">
                    {intl.formatMessage({ id: 'home.benefits.card.description' })}
                  </p>
                  <Link to={getLocalizedPath('/cropper')} className="bg-white text-primary-600 font-semibold py-3 px-6 rounded-lg hover:bg-gray-100 active:bg-gray-200 transition-all duration-200 flex items-center space-x-2 touch-manipulation text-sm sm:text-base">
                    <span>{intl.formatMessage({ id: 'home.benefits.card.button' })}</span>
                    <ArrowRight className="h-4 w-4 sm:h-5 sm:w-5" />
                  </Link>
                </div>
              </motion.div>
            </div>
          </div>
        </section>

        {/* Testimonials Section */}
        <section className="py-12 sm:py-16 lg:py-20 gradient-bg">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className="text-center mb-12 sm:mb-16"
            >
              <h2 className="text-2xl sm:text-3xl md:text-4xl font-display font-bold text-gray-900 mb-3 sm:mb-4 leading-tight">
                {intl.formatMessage({ id: 'home.testimonials.title' })}
              </h2>
              <p className="text-base sm:text-lg lg:text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
                {intl.formatMessage({ id: 'home.testimonials.description' })}
              </p>
            </motion.div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
              {testimonials.map((testimonial, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8, delay: index * 0.2 }}
                  viewport={{ once: true }}
                  className="bg-white rounded-xl p-5 sm:p-6 shadow-lg hover:shadow-xl transition-all duration-300 touch-manipulation"
                >
                  <div className="flex items-center mb-3 sm:mb-4">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <Star key={i} className="h-4 w-4 sm:h-5 sm:w-5 text-yellow-400 fill-current" />
                    ))}
                  </div>
                  <p className="text-sm sm:text-base text-gray-700 mb-4 sm:mb-6 italic leading-relaxed">"{testimonial.text}"</p>
                  <div>
                    <h4 className="text-sm sm:text-base font-semibold text-gray-900 leading-tight">{testimonial.name}</h4>
                    <p className="text-xs sm:text-sm text-gray-600">{testimonial.role}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-12 sm:py-16 lg:py-20 bg-gray-900 text-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <h2 className="text-2xl sm:text-3xl md:text-4xl font-display font-bold mb-4 sm:mb-6 leading-tight">
                {intl.formatMessage({ id: 'home.cta.title' })}
              </h2>
              <p className="text-base sm:text-lg lg:text-xl text-gray-300 mb-6 sm:mb-8 max-w-3xl mx-auto leading-relaxed">
                {intl.formatMessage({ id: 'home.cta.description' })}
              </p>
              <Link to={getLocalizedPath('/cropper')} className="bg-primary-600 hover:bg-primary-700 active:bg-primary-800 text-white font-semibold py-3 sm:py-4 px-6 sm:px-8 rounded-lg text-base sm:text-lg transition-all duration-200 flex items-center space-x-2 mx-auto touch-manipulation">
                <Crop className="h-5 w-5 sm:h-6 sm:w-6" />
                <span>{intl.formatMessage({ id: 'home.cta.button' })}</span>
                <ArrowRight className="h-5 w-5 sm:h-6 sm:w-6" />
              </Link>
            </motion.div>
          </div>
        </section>
      </motion.div>
    </>
  )
}

export default Home
